<template>
  <div class="text-editor">
    <artefact-dialog
      v-if="artefactCommand"
      :object="artefactObject"
      @close="closeArtefactDialog"
      @add="addArtefact"
    />

    <label v-if="label" class="text-editor-label" :class="{ required: required}">{{ label }}</label>
    <div class="text-editor-menubar-sticky d-flex flex-row" :style="`--sticky-offset: ${stickyOffset}px`">
      <editor-menu-bar
        class="flex-grow-1"
        v-if="!readonly"
        :editor="editor"
        v-slot="{ commands, isActive, getMarkAttrs }"
      >
        <div
          class="text-editor-menubar-wrapper"
          v-bind:class="{'text-editor-menubar-extended': isActive.table()}"
        >
          <div class="text-editor-menubar-inner elevation-5">
            <v-toolbar class="text-editor-menubar text-editor-menubar-primary" height="40" flat dense>
              <v-btn :class="{ 'v-btn--active': isActive.bold() }" @click="commands.bold" text>
                <v-icon>mdi-format-bold</v-icon>
              </v-btn>

              <v-btn :class="{ 'v-btn--active': isActive.italic() }" @click="commands.italic" text>
                <v-icon>mdi-format-italic</v-icon>
              </v-btn>

              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :class="{
                      'v-btn--active': isActive.strike() ||
                        isActive.underline() ||
                        isActive.superscript() ||
                        isActive.subscript() ||
                        isActive.code()
                    }"
                    v-bind="attrs"
                    v-on="on"
                    text
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list class="pa-0" dense>
                  <v-list-item class="pa-2">
                    <v-btn class="rounded-0 mr-1" :class="{ 'v-btn--active': isActive.strike() }"
                           @click="commands.strike" text icon>
                      <v-icon class="pa-0">mdi-format-strikethrough</v-icon>
                    </v-btn>

                    <v-btn class="rounded-0 mr-1" :class="{ 'v-btn--active': isActive.underline() }"
                           @click="commands.underline" text icon>
                      <v-icon>mdi-format-underline</v-icon>
                    </v-btn>

                    <v-btn class="rounded-0 mr-1" :class="{ 'v-btn--active': isActive.superscript() }"
                           @click="commands.superscript()" text icon>
                      <v-icon>mdi-format-superscript</v-icon>
                    </v-btn>

                    <v-btn class="rounded-0" :class="{ 'v-btn--active': isActive.subscript() }"
                           @click="commands.subscript()" text icon>
                      <v-icon>mdi-format-subscript</v-icon>
                    </v-btn>

                    <v-btn class="rounded-0" :class="{ 'v-btn--active': isActive.code() }"
                           @click="commands.code" text icon>
                      <v-icon>mdi-code-braces</v-icon>
                    </v-btn>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-divider class="mx-2" vertical></v-divider>

              <v-btn :class="{ 'v-btn--active': isActive.paragraph() }" @click="commands.paragraph" text>
                <v-icon>mdi-format-paragraph</v-icon>
              </v-btn>

              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :class="{
                      'v-btn--active': isActive.heading({ level: 1 }) ||
                        isActive.heading({ level: 2 }) ||
                        isActive.heading({ level: 3 }) ||
                        isActive.heading({ level: 4 }) ||
                        isActive.heading({ level: 5 })
                    }"
                    v-bind="attrs"
                    v-on="on"
                    text
                  >
                    <v-icon>mdi-format-title</v-icon>
                  </v-btn>
                </template>
                <v-list class="pa-0" dense>
                  <v-list-item class="pa-2">
                    <v-btn
                      class="rounded-0" :class="{ 'v-btn--active': isActive.heading({ level: 1 }) }"
                      @click="commands.heading({ level: 1 })" text icon>
                      <v-icon>mdi-format-header-1</v-icon>
                    </v-btn>

                    <v-btn
                      class="rounded-0" :class="{ 'v-btn--active': isActive.heading({ level: 2 }) }"
                      @click="commands.heading({ level: 2 })" text icon>
                      <v-icon>mdi-format-header-2</v-icon>
                    </v-btn>

                    <v-btn
                      class="rounded-0" :class="{ 'v-btn--active': isActive.heading({ level: 3 }) }"
                      @click="commands.heading({ level: 3 })" text icon>
                      <v-icon>mdi-format-header-3</v-icon>
                    </v-btn>

                    <v-btn
                      class="rounded-0" :class="{ 'v-btn--active': isActive.heading({ level: 4 }) }"
                      @click="commands.heading({ level: 4 })" text icon>
                      <v-icon>mdi-format-header-4</v-icon>
                    </v-btn>

                    <v-btn
                      class="rounded-0" :class="{ 'v-btn--active': isActive.heading({ level: 5 }) }"
                      @click="commands.heading({ level: 5 })" text icon>
                      <v-icon>mdi-format-header-5</v-icon>
                    </v-btn>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-divider class="mx-2" vertical></v-divider>

              <v-btn :class="{ 'v-btn--active': isActive.bullet_list() }" @click="commands.bullet_list" text>
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-btn>

              <v-btn :class="{ 'v-btn--active': isActive.ordered_list() }" @click="commands.ordered_list" text>
                <v-icon>mdi-format-list-numbered</v-icon>
              </v-btn>

              <v-btn @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })" text>
                <v-icon>mdi-table</v-icon>
              </v-btn>

              <v-btn @click="showLinkPrompt(commands.link, getMarkAttrs('link'))" text>
                <v-icon>mdi-link</v-icon>
              </v-btn>

              <v-btn @click="showImagePrompt(commands.image)" text>
                <v-icon>mdi-image</v-icon>
              </v-btn>

              <v-btn @click="openArtefactDialog(commands.artefact)" text>
                <v-icon>mdi-shape-plus</v-icon>
              </v-btn>

              <v-divider class="mx-2" vertical></v-divider>

              <v-btn :class="{ 'v-btn--active': isActive.code_block() }" @click="commands.code_block" text>
                <v-icon>mdi-code-tags</v-icon>
              </v-btn>

              <v-btn :class="{ 'v-btn--active': isActive.blockquote() }" @click="commands.blockquote" text>
                <v-icon>mdi-format-quote-close</v-icon>
              </v-btn>

              <v-divider class="mx-2" vertical></v-divider>

              <v-btn @click="commands.undo" text>
                <v-icon>mdi-undo</v-icon>
              </v-btn>

              <v-btn @click="commands.redo" text>
                <v-icon>mdi-redo</v-icon>
              </v-btn>

              <v-spacer/>
              <slot name="append"></slot>
            </v-toolbar>

            <v-toolbar class="text-editor-menubar text-editor-menubar-secondary" height="40" flat dense>
              <v-btn @click="commands.deleteTable" text>
                <v-icon>mdi-table-remove</v-icon>
              </v-btn>

              <v-btn @click="commands.addColumnBefore" text>
                <v-icon>mdi-table-column-plus-before</v-icon>
              </v-btn>

              <v-btn @click="commands.addColumnAfter" text>
                <v-icon>mdi-table-column-plus-after</v-icon>
              </v-btn>

              <v-btn @click="commands.deleteColumn" text>
                <v-icon>mdi-table-column-remove</v-icon>
              </v-btn>

              <v-btn @click="commands.addRowBefore" text>
                <v-icon>mdi-table-row-plus-before</v-icon>
              </v-btn>

              <v-btn @click="commands.addRowAfter" text>
                <v-icon>mdi-table-row-plus-after</v-icon>
              </v-btn>

              <v-btn @click="commands.deleteRow" text>
                <v-icon>mdi-table-row-remove</v-icon>
              </v-btn>

              <v-btn @click="commands.toggleCellMerge" text>
                <v-icon>mdi-table-merge-cells</v-icon>
              </v-btn>
            </v-toolbar>
          </div>
        </div>
      </editor-menu-bar>

      <div class="flex-shrink-0 append-outer-slot">
        <slot name="append-outer"></slot>
      </div>
    </div>

    <TextEditorRenderer>
      <editor-content
        class="text-editor-content"
        :editor="editor"
        :style="editorContentStyles"
      />
    </TextEditorRenderer>

    <VMessages :style="showDetails ? '' : 'opacity: 0;'" :value="errorBucket" color="error" class="mt-2"/>

    <div v-if="confirmationRequired && hasChanges" class="confirmation-required-wrapper">
      <div class="confirmation-required-buttons">
        <v-btn class="mr-2" icon fab small @click="cancelChanges">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-btn color="primary" fab small @click="saveChanges">
          <v-icon>mdi-check</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
// Import the basic building blocks
import { Editor, EditorContent, EditorMenuBar, Extension } from 'tiptap'
import Artefact from './Plugins/ArtefactPlugin'
import Code from './Plugins/CodePlugin'
import Heading from './Plugins/HeadingPlugin'
import Image from './Plugins/ImagePlugin'
import Paragraph from './Plugins/ParagraphPlugin'
import Strike from './Plugins/StrikePlugin'
import Subscript from './Plugins/SubscriptPlugin'
import Superscript from './Plugins/SuperscriptPlugin'
import {
  Blockquote,
  Bold,
  BulletList,
  CodeBlock,
  HardBreak,
  History,
  HorizontalRule,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Placeholder,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  TodoItem,
  TodoList,
  Underline
} from 'tiptap-extensions'
import VInput from 'vuetify/lib/components/VInput/VInput.js'
import { debounce } from 'lodash'
import ArtefactDialog from '@/components/Utilities/TextEditor/ArtefactDialog.vue'

export default {
  name: 'TextEditor',

  extends: VInput,

  /**
   * Properties.
   */
  props: {
    value: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    validateOnBlur: {
      type: Boolean,
      required: false,
      default: true
    },
    hideDetails: {
      type: [String, Boolean],
      required: false,
      default: 'auto'
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    confirmationRequired: {
      type: Boolean,
      required: false,
      default: false
    },
    // TextEditor emits (debounced) onUpdate instead of onBlur
    emitOnInput: {
      type: Boolean,
      required: false,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: undefined
    },
    minHeight: {
      type: [Number, String],
      required: false,
      default: 160
    },
    maxHeight: {
      type: [Number, String],
      required: false
    },
    stickyOffset: {
      required: false,
      type: Number,
      default: 0
    }
  },

  /**
   * Components.
   */
  components: {
    ArtefactDialog,
    EditorContent,
    EditorMenuBar
  },

  /**
   * Data variables.
   */
  data () {
    return {
      currentValue: '',
      hasChanges: false,
      artefactObject: null,
      artefactCommand: null,
      // Create an `Editor` instance with some default content. The editor is
      // then passed to the `EditorContent` component as a `prop`
      editor: new Editor({
        editable: false,
        extensions: [
          new Artefact(),
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Paragraph(),
          new Heading({ levels: [1, 2, 3, 4, 5] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Image(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new Superscript(),
          new Subscript(),
          new History(),
          new Table({
            resizable: true
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow(),
          new Placeholder({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            emptyNodeText: this.placeholder,
            showOnlyWhenEditable: false,
            showOnlyCurrent: false
          })
        ],
        nativeExtensions: [
          new class extends Extension {
            keys () {
              return {
                Enter () {
                  this.insertText('    ')
                }
              }
            }
          }()
        ],
        content: this.value,
        onUpdate: ({ getHTML }) => {
          // Set current value.
          this.currentValue = getHTML()
          // Set has changes to true.
          this.hasChanges = true
          // Emit
          if (this.emitOnInput && !this.touched) {
            this.touched = true
            this.debouncedEmit()
          }
          // Set has focused to false.
          this.resetValidation()
        },
        onBlur: ({ event }) => {
          // Emit blur event.
          this.$emit('blur', event)

          if (this.confirmationRequired === false) {
            // Emit current html input value.
            this.$emit('input', this.currentValue)
          }
        },
        onFocus: ({ event }) => {
          // Emit focus event.
          this.$emit('focus', event)
        }
        // onDrop: ({ event }) => {
        //   alert('drop')
        // },
        // onPaste: ({ event }) => {
        //   alert('paste')
        // }
      }),
      touched: false
    }
  },

  computed: {
    editorContentStyles () {
      const minHeightUnit = this.minHeight && !String(this.minHeight)?.match(/px|em|rem|vw|vh/) ? 'px' : ''
      const maxHeightUnit = this.maxHeight && !String(this.maxHeight)?.match(/px|em|rem|vw|vh/) ? 'px' : ''

      const minHeight = this.minHeight
        ? `--min-height: ${this.minHeight}${minHeightUnit};`
        : ''
      const maxHeight = this.maxHeight
        ? `--max-height: ${this.maxHeight}${maxHeightUnit}; overflow-y: auto;`
        : ''

      return `${minHeight} ${maxHeight}`
    }
  },

  /**
   * Watchers.
   */
  watch: {
    /**
     * Watch placeholder changes.
     */
    placeholder (value) {
      this.editor.setOptions({
        emptyNodeText: value
      })
    },

    /**
     * Watch value changes.
     */
    value: {
      immediate: true,
      handler (value) {
        // Check if current value is not matching with editor content.
        if (this.editor.getHTML() !== value) {
          // Set has changes to false.
          this.hasChanges = false
          // Set new content to editor.
          this.editor.setContent(value)
          // Set current value.
          this.currentValue = value
        }
      }
    },

    /**
     * Watch readonly status changes.
     */
    readonly: {
      immediate: true,
      handler () {
        this.editor.setOptions({
          editable: !this.readonly
        })
      }
    }
  },

  mounted () {
    // Add artefact click handler.
    Artefact.addClickHandler(({ id, version, type, href, route, callback }) => {
      // Open artefact dialog and apply callback.
      this.openArtefactDialog(callback, type, id, version)
    })
  },

  /**
   * Before destroy event.
   */
  beforeDestroy () {
    // Always destroy your editor instance when it's no longer needed
    this.editor.destroy()
  },

  /**
   * Methods.
   */
  methods: {
    /**
     * Cancel changes.
     */
    cancelChanges () {
      // Set new content to editor.
      this.editor.setContent(this.value)
      // Set has changes to false.
      this.hasChanges = false
      // Emit blur event.
      this.$emit('blur', event)
    },

    /**
     * Save edit value.
     */
    saveChanges () {
      // Emit current html input value.
      this.$emit('input', this.currentValue)
      // Set has changes to false.
      this.hasChanges = false
      // Emit blur event.
      this.$emit('blur', event)
    },

    /**
     * Show image prompt.
     */
    showImagePrompt (command) {
      // Show prompt and ask for image url.
      const src = prompt('Enter the url of your image here')
      // Check if src is not null.
      if (src !== null) {
        // Perform command to add image to text editor.
        command({ src })
      }
    },

    /**
     * Show link prompt.
     */
    showLinkPrompt (command, attributes) {
      // Show prompt and ask for link url.
      const src = prompt('Enter the url of your link here', attributes.src)
      // Check if src is not null.
      if (src !== null) {
        // Perform command to add link to text editor.
        command({ href: src })
      }
    },

    /**
     * Open artefact dialog.
     */
    openArtefactDialog (command, type = null, id = null, version = null) {
      this.artefactObject = { type, id, version }
      this.artefactCommand = command
    },

    closeArtefactDialog () {
      this.artefactObject = null
      this.artefactCommand = null
    },

    addArtefact (types) {
      if (this.artefactCommand) {
        this.artefactCommand(types)
        this.closeArtefactDialog()
      }
    },

    /**
     * Focus editor.
     */
    focus () {
      // Focus editor.
      this.editor.focus()
    },
    debouncedEmit: debounce(function () {
      this.touched = false
      this.$emit('input', this.currentValue)
    }, 600)
  }
}
</script>

<style lang="scss" scoped>
.text-editor .text-editor-menubar-sticky {
  top: calc(var(--sticky-offset) + 50px) !important;
}

.text-editor {
  border-bottom: 2px solid lightgrey;

  .text-editor-label {
    display: block;
    font-size: 12px;
  }

  .text-editor-label.required::after {
    content: "*";
    font-weight: bold;
    color: var(--v-error-base);
  }

  .text-editor-menubar-sticky {
    position: sticky;
    left: 0;
    top: 48px;
    z-index: 1;
  }

  .text-editor-menubar-wrapper {
    position: relative;
    min-height: 40px;
    margin-bottom: 16px;

    &,
    .text-editor-menubar-inner,
    .text-editor-menubar-primary,
    .text-editor-menubar-secondary {
      transition: all 0.25s ease-in-out;
    }

    ::v-deep .text-editor-menubar {
      position: absolute;
      width: 100%;
      top: 0;

      .v-toolbar__content {
        padding: 4px;
      }
    }

    .text-editor-menubar-inner {
      position: absolute;
      top: 0;
      height: 40px;
      width: 100%;
      overflow: hidden;
    }

    &:not(.text-editor-menubar-extended) .text-editor-menubar-inner {
      box-shadow: none !important;
    }

    .text-editor-menubar-primary {
      position: absolute;
      bottom: auto;
      top: 0;
      z-index: 2;
    }

    .text-editor-menubar-secondary {
      position: absolute;
      bottom: 0;
      top: auto;
      z-index: 1;
      opacity: 0;
    }

    &.text-editor-menubar-extended {
      margin-bottom: 56px;

      .text-editor-menubar-inner {
        height: 80px;
      }

      .text-editor-menubar-secondary {
        opacity: 1;
      }
    }
  }

  ::v-deep .text-editor-menubar {
    background: var(--v-topbottomstrip-base) !important;
    color: var(--v-primary-base);

    .v-btn {
      min-width: auto !important;
      padding: 0 4px !important;
      margin: 2px !important;
      border-radius: 0;
      height: 28px;

      &.v-btn--active {
        opacity: 1;
        color: inherit;
      }
    }
  }

  ::v-deep .text-editor-content {
    color: rgba(0, 0, 0, 0.87);

    > .ProseMirror, > [contenteditable] {
      outline: 0;
      min-height: var(--min-height);
      max-height: var(--max-height);
    }
  }

  .append-outer-slot {
    padding-top: 2px;
  }

  .confirmation-required-wrapper {
    position: relative;

    .confirmation-required-buttons {
      position: absolute;
      right: 0;
      bottom: 30px;
    }
  }
}
</style>

<style lang="scss">
.v-dialog .v-card .v-card__text {
  position: relative;

  .text-editor .text-editor-menubar-sticky {
    top: var(--sticky-offset) !important;
  }
}
</style>
